import { useTranslation } from 'react-i18next';
import type { AdminUserStatus, IAdminUser } from '@apis/adminUsers';
import { Pill } from '@components/ui/Pill/Pill';
import table from '../../adminTable.module.css';
import styles from './UsersTable.module.css';

// The Status and MFA columns. Both facts come from a read that only decorates the
// roster: an unknown value (the read was unavailable) renders "—", never "no".

const STATUS_TONES: Record<AdminUserStatus, string | undefined> = {
  active: styles.statusActive,
  invited: styles.statusInvited,
  invitationExpired: styles.statusExpired,
};

interface IMemberFactCellProps {
  user: IAdminUser;
}

function UnknownCell() {
  return <td className={`${table.tdCenter} ${table.muted}`}>—</td>;
}

/** Active / Invited / Invitation expired — the last in a warning tone: the admin should resend. */
export function StatusCell(props: IMemberFactCellProps) {
  const { status } = props.user;
  const { t } = useTranslation();
  if (!status) return <UnknownCell />;
  return (
    <td className={table.tdCenter}>
      <Pill className={STATUS_TONES[status]}>{t(`adminUsers.status.${status}`)}</Pill>
    </td>
  );
}

/**
 * On (with the enrolled methods) / Off / Via the company's IdP. Only meaningful
 * once the member has signed in: before that there is nothing enrolled to show.
 */
export function MfaCell(props: IMemberFactCellProps) {
  const { status, mfa, mfaMethods = [], ssoConnectionName } = props.user;
  const { t } = useTranslation();
  if (status !== 'active' || !mfa) return <UnknownCell />;
  if (mfa === 'identityProvider') {
    return (
      <td className={table.tdCenter}>
        {ssoConnectionName
          ? t('adminUsers.mfa.identityProvider', { name: ssoConnectionName })
          : t('adminUsers.mfa.identityProviderGeneric')}
      </td>
    );
  }
  if (mfa === 'disabled') {
    return <td className={`${table.tdCenter} ${table.muted}`}>{t('adminUsers.mfa.disabled')}</td>;
  }
  return (
    <td className={table.tdCenter}>
      <span className={styles.stackCell}>
        <span>{t('adminUsers.mfa.enabled')}</span>
        {mfaMethods.length > 0 && (
          <span className={styles.subtext}>
            {/* The method names the sign-in page and Security settings already use. */}
            {mfaMethods.map((m) => t(`auth.mfa.method.${m}`)).join(t('adminUsers.mfa.methodSeparator'))}
          </span>
        )}
      </span>
    </td>
  );
}
